import {
  Button,
  Enums,
  PasswordTextfield,
  withStyles,
} from "@envestnet/envreact-component-library";
import { faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";
import { faExclamationCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AuthenticatorWidget from "components/AuthenticatorWidget";
import React, { useCallback, useEffect, useState, useMemo } from "react";
import withIntl from "utils/withIntl";

import CentricLayout from "components/CentricLayout";
import RemoveDialogWarning from "components/RemoveDialogWarning";
import { AuthenticatorConfiguration } from "types/Mfa";
import { isTodayDate } from "utils/formatters";
import { UserDetails, UserInfo } from "types/UserInfo";
import messages from "./messages";
import styles, { Styles } from "./styles";
import { AppAuthenticatorType } from "./types";

const { ButtonStyle, ButtonSize } = Enums;

interface InjectedProps extends Styles {
  /** Internal - injected from withIntl */
  formatMessage: FormatMessage;

  logout: () => void;

  userDetails: UserDetails;
  userInfo: UserInfo;
}

interface DefaultProps {}

export interface MfaProps extends DefaultProps {
  currentStep: string;
  loading: boolean;
  appAuthenticatorSetupDone: boolean;
  isPasswordValidationDone: boolean;
  showError: boolean;
  hasNoAuthenticators: boolean;
  authenticatorsInfo: Array<AppAuthenticatorType>;
  hasUpdatedTnCAccepted: boolean;
  tncForCidpUser: string | null;

  onLoading: () => void;
  onRemoveLoading: (step: string) => void;
  isRedirectEnable: (value: boolean) => void;
  goToOktaVerifyScreen: () => void;
  goToAppAuthenticatorScreen: () => void;
  goToAppPhoneScreen: () => void;
  goToEmailOtpScreen: () => void;
  goToEnvAuthenticatorScreen: () => void;
  goToDashboard: () => void;
  goToSecuritySettings: () => void;
  resetState: Function;
  onPasswordValidate: Function;
  goBackTo: () => void;
  onResetError: Function;
  getTnCForCidpUser: () => void;
  setUserAcceptedNewTnC: () => void;
}

const defaultProps: DefaultProps = {};

type Props = InjectedProps & MfaProps;

const DashboardMfa = (props: Props) => {
  const {
    classes,
    formatMessage,
    onLoading,
    authenticatorsInfo,
    isRedirectEnable,
    appAuthenticatorSetupDone,
    onRemoveLoading,
    goToOktaVerifyScreen,
    goToAppAuthenticatorScreen,
    goToEnvAuthenticatorScreen,
    goToAppPhoneScreen,
    goToEmailOtpScreen,
    goToDashboard,
    currentStep,
    onPasswordValidate,
    isPasswordValidationDone,
    goBackTo,
    onResetError,
    showError,
    resetState,
    hasNoAuthenticators,
    loading,
    userDetails,
    userInfo,
    getTnCForCidpUser,
  } = props;
  const [password, setPassword] = useState("");
  const [step, setStep] = useState("initial");
  const [removeAuth, setRemoveAuth] = useState("");
  const [passwordNextDisabled, setPasswordNextDisabled] =
    useState<boolean>(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const isEmailOtpEnabled = useMemo(
    () => userDetails.emailOtpEnabled,
    [userDetails]
  );

  useEffect(() => {
    if (userInfo.cidpId !== "" && userDetails.cidpId !== "") {
      if (userDetails.directLoginEnabled) {
        onLoading();
      } else {
        goToDashboard();
      }
    }
  }, [userDetails, userInfo]);

  useEffect(() => {
    if ((userInfo as UserInfo).email !== "") {
      getTnCForCidpUser();
    }
  }, [userInfo]);

  const onChangeNewPasswordConfirmed = (value) => {
    if (value) {
      setPasswordNextDisabled(false);
    } else {
      setPasswordNextDisabled(true);
    }
    if (showError) {
      onResetError();
    }
    setPassword(value);
  };

  useEffect(
    () => () => {
      isRedirectEnable(false);
    },
    []
  );

  useEffect(() => {
    if (currentStep !== step) {
      setStep(currentStep);
    }
  }, [currentStep, step]);

  useEffect(() => {
    if (isPasswordValidationDone) {
      setStep("mfa");
    }
  }, [isPasswordValidationDone]);

  useEffect(() => {
    if (appAuthenticatorSetupDone) {
      onLoading();
    }
  }, [appAuthenticatorSetupDone]);

  const onCancelClick = () => {
    goBackTo();
    resetState("initial");
  };

  const removeConfig = (remove: AuthenticatorConfiguration, goTo: Function) => {
    if (remove === AuthenticatorConfiguration.Remove) {
      setShowConfirmModal(true);
      return;
    }
    goTo();
  };

  const onModalClose = () => {
    setShowConfirmModal(false);
    isRedirectEnable(false);
  };

  const onNext = () => {
    if (password) {
      onPasswordValidate(password);
    }
  };

  const onModalSuccess = () => {
    onRemoveLoading(removeAuth);
    setShowConfirmModal(false);
  };

  const currentAppAvailable = (type: string) => {
    const data = authenticatorsInfo?.find(
      (row: AppAuthenticatorType) => row.type === type
    );
    return !!data;
  };

  const configureStatus = useCallback(
    (type: string) => {
      const data = authenticatorsInfo?.find(
        (row: AppAuthenticatorType) => row.type === type
      );
      if (
        type !== "email" &&
        data?.enabled &&
        data?.lastUpdatedOn &&
        isTodayDate(data?.lastUpdatedOn)
      ) {
        // some backend flag or timestamp
        return AuthenticatorConfiguration.Done;
      }
      if (data?.enabled) {
        return AuthenticatorConfiguration.Remove;
      }
      return AuthenticatorConfiguration.Initial;
    },
    [authenticatorsInfo]
  );

  const appAuth = configureStatus("app");

  const oktaAuth = configureStatus("okta");

  const phoneAuth = configureStatus("phone");

  const envAuth = configureStatus("envapp");

  const emailAuth = configureStatus("email");

  return (
    <>
      <div className={classes.container}>
        <div className={classes.header4}>
          <div className={classes.iconWrapper}>
            <span className="header-font-family">
              {formatMessage(messages.header)}
            </span>
          </div>
        </div>
        {step === "initial" && (
          <CentricLayout className={classes.mfaCenter}>
            <>
              <div data-testid="mfa-password">
                <PasswordTextfield
                  aria-required
                  className={classes.textfield}
                  label={formatMessage(messages.pText)}
                  onChangeText={onChangeNewPasswordConfirmed}
                  text={password}
                  textFieldStyle={{ borderRadius: 0 }}
                  errorMessage={
                    showError ? formatMessage(messages.passwordError) : null
                  }
                />
              </div>
              <div data-testid="mfa-footer" className={classes.footer}>
                <Button
                  buttonStyle={ButtonStyle.Primary}
                  buttonSize={ButtonSize.Regular}
                  className={classes.next}
                  name={formatMessage(messages.next)}
                  onClick={() => onNext()}
                  disabled={passwordNextDisabled}
                  aria-label="MFA Next"
                />
                <Button
                  buttonStyle={ButtonStyle.Secondary}
                  buttonSize={ButtonSize.Regular}
                  className={classes.cancel}
                  name={formatMessage(messages.cancelButtonLabel)}
                  onClick={() => onCancelClick()}
                  aria-label="MFA Cancel"
                />
              </div>
            </>
          </CentricLayout>
        )}
        {step === "mfa" && (
          <CentricLayout className={classes.mfaCenter}>
            <div className={classes.containerMfa}>
              <>
                {currentAppAvailable("app") && (
                  <AuthenticatorWidget
                    type="app"
                    configurationStatus={appAuth}
                    goToAppAuthenticatorScreen={() => {
                      removeConfig(appAuth, goToAppAuthenticatorScreen);
                      setRemoveAuth("app");
                    }}
                    isLoading={loading && removeAuth === "app"}
                    isSpinner
                  />
                )}

                {currentAppAvailable("okta") && (
                  <AuthenticatorWidget
                    type="okta"
                    configurationStatus={oktaAuth}
                    goToOktaVerifyScreen={() => {
                      removeConfig(oktaAuth, goToOktaVerifyScreen);
                      setRemoveAuth("okta");
                    }}
                    isLoading={loading && removeAuth === "okta"}
                    isSpinner
                  />
                )}

                {currentAppAvailable("phone") && (
                  <AuthenticatorWidget
                    type="phone"
                    configurationStatus={phoneAuth}
                    goToAppPhoneScreen={() => {
                      removeConfig(phoneAuth, goToAppPhoneScreen);
                      setRemoveAuth("phone");
                    }}
                    isLoading={loading && removeAuth === "phone"}
                    isSpinner
                  />
                )}

                {currentAppAvailable("envapp") && (
                  <AuthenticatorWidget
                    type="envapp"
                    configurationStatus={envAuth}
                    goToEnvAppScreen={() => {
                      removeConfig(envAuth, goToEnvAuthenticatorScreen);
                      setRemoveAuth("envapp");
                    }}
                    isLoading={loading && removeAuth === "envapp"}
                    isSpinner
                  />
                )}

                {isEmailOtpEnabled && currentAppAvailable("email") && (
                  <AuthenticatorWidget
                    type="email"
                    configurationStatus={emailAuth}
                    goToEmailOtpScreen={() => {
                      removeConfig(emailAuth, goToEmailOtpScreen);
                      setRemoveAuth("email");
                    }}
                    isLoading={loading && removeAuth === "email"}
                    isSpinner
                  />
                )}
              </>

              <div data-testid="mfa-footer" className={classes.footer}>
                <div className={classes.footerWithError}>
                  {hasNoAuthenticators ? (
                    <div className={classes.noAuthenticatorsError}>
                      <FontAwesomeIcon
                        icon={faExclamationCircle}
                        className={classes.noAuthenticatorsErrorIcon}
                      />
                      <span>
                        {formatMessage(messages.noAuthenticatorsError)}
                      </span>
                    </div>
                  ) : (
                    <div></div>
                  )}
                  <Button
                    buttonStyle={ButtonStyle.Primary}
                    buttonSize={ButtonSize.Regular}
                    className={classes.cancel}
                    disabled={hasNoAuthenticators}
                    name={formatMessage(messages.done)}
                    onClick={() => onCancelClick()}
                    aria-label="MFA Done"
                  />
                </div>
              </div>
            </div>
          </CentricLayout>
        )}
      </div>

      <RemoveDialogWarning
        open={showConfirmModal}
        setOpen={onModalClose}
        setSuccess={onModalSuccess}
      >
        <FontAwesomeIcon
          icon={faTriangleExclamation}
          style={{
            color: "#F8A72D",
            position: "absolute",
            top: 24,
            height: 36,
            width: 36,
          }}
        />
        <h3 className={classes.modalHeader}>
          {formatMessage(messages.removeMFA)}
        </h3>
      </RemoveDialogWarning>
    </>
  );
};

DashboardMfa.defaultProps = defaultProps;
export default withStyles(styles)(withIntl(DashboardMfa));
